import { create } from 'zustand';
import type { BatteryStatus, HeartbeatItem, WarningItem } from '@shared/lib/api/types';
import type { RobotPose } from '@shared/lib/api/navigation';
import type { Robot } from '@/lib/robots-api';

// Persist the user's camera selection across reloads. The kiosk reloads
// chromium on every dashboard restart and engineers reload Tailscale
// browsers regularly — losing the selection every time is annoying.
const SELECTED_CAMERAS_KEY = 'verne.selectedCameraIds';

function loadSelectedCameraIds(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(SELECTED_CAMERAS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function saveSelectedCameraIds(ids: string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SELECTED_CAMERAS_KEY, JSON.stringify(ids));
  } catch {
    /* quota or disabled storage — non-fatal */
  }
}

type RobotStore = {
  fleet: Robot[];
  fleetLoading: boolean;
  setFleet: (robots: Robot[]) => void;
  setFleetLoading: (loading: boolean) => void;

  connected: boolean;
  robotOnline: boolean;
  battery: BatteryStatus | null;
  navPose: RobotPose | null;
  navPath: Array<[number, number]>;
  mapRevision: number;
  stateMachineState: string | null;
  recoveryDone: boolean;
  healingInProgress: boolean;
  navStatus: string | null;
  baseOwner: string | null;
  teleopUser: string | null;
  estopActive: boolean;
  positionKnown: boolean | null;
  localizationDone: boolean | null;
  navAvailable: boolean;
  cameras: Array<{ id: string; name: string }>;
  cameraViewerActive: boolean;
  selectedCameraIds: string[];
  heartbeats: HeartbeatItem[];
  warnings: WarningItem[];
  lastFullStatusAt: number | null;

  setConnected: (connected: boolean) => void;
  setRobotOnline: (online: boolean) => void;
  setBattery: (battery: BatteryStatus | null) => void;
  setNavPose: (pose: RobotPose | null) => void;
  setNavPath: (path: Array<[number, number]>) => void;
  setMapRevision: (rev: number) => void;
  setStateMachineState: (state: string | null) => void;
  setRecoveryDone: (done: boolean) => void;
  setHealingInProgress: (val: boolean) => void;
  setNavStatus: (status: string | null) => void;
  setBaseOwner: (owner: string | null) => void;
  setTeleopUser: (user: string | null) => void;
  setEstopActive: (active: boolean) => void;
  setPositionKnown: (val: boolean | null) => void;
  setLocalizationDone: (val: boolean | null) => void;
  setNavAvailable: (val: boolean) => void;
  setCameras: (cameras: Array<{ id: string; name: string }>) => void;
  setCameraViewerActive: (active: boolean) => void;
  toggleCameraSelection: (id: string) => void;
  selectAllCameras: () => void;
  clearSelectedCameras: () => void;
  setHeartbeats: (hbs: HeartbeatItem[]) => void;
  setWarnings: (warnings: WarningItem[]) => void;
  setLastFullStatusAt: (ts: number | null) => void;
};

export const useRobotStore = create<RobotStore>((set) => ({
  fleet: [],
  fleetLoading: true,
  setFleet: (fleet) => set({ fleet }),
  setFleetLoading: (fleetLoading) => set({ fleetLoading }),

  connected: false,
  robotOnline: false,
  battery: null,
  navPose: null,
  navPath: [],
  mapRevision: 0,
  stateMachineState: null,
  recoveryDone: false,
  healingInProgress: false,
  navStatus: null,
  baseOwner: null,
  teleopUser: null,
  estopActive: false,
  positionKnown: null,
  localizationDone: null,
  navAvailable: false,
  cameras: [],
  cameraViewerActive: false,
  selectedCameraIds: loadSelectedCameraIds(),
  heartbeats: [],
  warnings: [],
  lastFullStatusAt: null,

  setConnected: (connected) => set({ connected }),
  setRobotOnline: (online) => set({ robotOnline: online }),
  setBattery: (battery) => set({ battery }),
  setNavPose: (navPose) => set({ navPose }),
  setNavPath: (navPath) => set({ navPath }),
  setMapRevision: (mapRevision) => set({ mapRevision }),
  setStateMachineState: (stateMachineState) => set({ stateMachineState }),
  setRecoveryDone: (recoveryDone) => set({ recoveryDone }),
  setHealingInProgress: (healingInProgress) => set({ healingInProgress }),
  setNavStatus: (navStatus) => set({ navStatus }),
  setBaseOwner: (baseOwner) => set({ baseOwner }),
  setTeleopUser: (teleopUser) => set({ teleopUser }),
  setEstopActive: (estopActive) => set({ estopActive }),
  setPositionKnown: (positionKnown) => set({ positionKnown }),
  setLocalizationDone: (localizationDone) => set({ localizationDone }),
  setNavAvailable: (navAvailable) => set({ navAvailable }),
  setCameras: (cameras) => set((state) => {
    const validIds = new Set(cameras.map((c) => c.id));
    const filtered = state.selectedCameraIds.filter((id) => validIds.has(id));
    const selectedCameraIds = filtered.length === 0 && state.cameras.length === 0
      ? cameras.map((c) => c.id)
      : filtered;
    saveSelectedCameraIds(selectedCameraIds);
    return { cameras, selectedCameraIds };
  }),
  setCameraViewerActive: (cameraViewerActive) => set({ cameraViewerActive }),
  toggleCameraSelection: (id) => set((state) => {
    const selectedCameraIds = state.selectedCameraIds.includes(id)
      ? state.selectedCameraIds.filter((x) => x !== id)
      : [...state.selectedCameraIds, id];
    saveSelectedCameraIds(selectedCameraIds);
    return { selectedCameraIds };
  }),
  selectAllCameras: () => set((state) => {
    const selectedCameraIds = state.cameras.map((c) => c.id);
    saveSelectedCameraIds(selectedCameraIds);
    return { selectedCameraIds };
  }),
  clearSelectedCameras: () => {
    saveSelectedCameraIds([]);
    return set({ selectedCameraIds: [] });
  },
  setHeartbeats: (heartbeats) => set({ heartbeats }),
  setWarnings: (warnings) => set({ warnings }),
  setLastFullStatusAt: (lastFullStatusAt) => set({ lastFullStatusAt }),
}));
